<script setup>
/**
 * The application bar: identity of the tool, the two destinations, the theme toggle and
 * who you are signed in as. Deliberately thin — it is chrome, not content.
 */
import { computed } from 'vue'
import OwnerAvatar from './OwnerAvatar.vue'
import { useTheme } from '@/composables/useTheme.js'
import { useAuthStore } from '@/stores/auth.js'
import { config, usesFakeApi } from '@/config.js'

const auth = useAuthStore()
const theme = useTheme()

const themeGlyph = computed(
  () => ({ auto: '◑', light: '☀', dark: '☾' })[theme.preference.value],
)

/**
 * Authentication can be off on *either* side, and the dangerous case is the one the SPA
 * cannot see for itself: a build with authentication enabled, pointed at an API running
 * with `AUTH_DISABLED`. `GET /me` reports it, so the flag reads both.
 */
const unauthenticated = computed(
  () => config.authDisabled || auth.identity?.authDisabled === true,
)

const unauthenticatedReason = computed(() =>
  config.authDisabled
    ? 'Authentication disabled in this build'
    : 'The API is running without authentication',
)
</script>

<template>
  <header class="header">
    <div class="header__inner">
      <RouterLink to="/" class="brand">
        <span class="brand__mark" aria-hidden="true">P</span>
        <span class="brand__name">PreviewApp</span>
      </RouterLink>

      <nav class="nav" aria-label="Main navigation">
        <RouterLink to="/" class="nav__link">Previews</RouterLink>
        <RouterLink to="/new" class="nav__link">Create</RouterLink>
      </nav>

      <div class="header__right">
        <span v-if="usesFakeApi" class="header__flag" title="No backend: seeded data">
          fake data
        </span>
        <span v-else-if="unauthenticated" class="header__flag" :title="unauthenticatedReason">
          auth disabled
        </span>

        <button
          type="button"
          class="btn btn--ghost btn--sm"
          :title="theme.label.value"
          @click="theme.cycle()"
        >
          <span aria-hidden="true">{{ themeGlyph }}</span>
          <span class="sr-only">{{ theme.label.value }}, change it</span>
        </button>

        <div v-if="auth.isAuthenticated" class="user">
          <OwnerAvatar :owner-label="auth.ownerLabel" :name="auth.displayName" :size="24" />
          <span class="user__name">
            {{ auth.displayName }}
            <span v-if="auth.isAdmin" class="user__admin">admin</span>
          </span>
          <button type="button" class="btn btn--ghost btn--sm" @click="auth.signOut()">
            Sign out
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
}

.header__inner {
  display: flex;
  align-items: center;
  gap: var(--sp-7);
  max-width: var(--page-max);
  min-height: var(--header-h);
  margin: 0 auto;
  padding: 0 var(--sp-6);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  color: var(--c-text);
  font-weight: var(--weight-semibold);
}

.brand:hover {
  text-decoration: none;
}

.brand__mark {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-md);
  background: var(--c-accent);
  color: #fff;
  font-size: var(--text-sm);
}

.nav {
  display: flex;
  gap: var(--sp-2);
}

.nav__link {
  padding: var(--sp-3) var(--sp-5);
  border-radius: var(--radius-md);
  color: var(--c-text-muted);
  font-weight: var(--weight-medium);
}

.nav__link:hover {
  background: var(--c-surface-3);
  color: var(--c-text);
  text-decoration: none;
}

.nav__link.router-link-exact-active {
  background: var(--c-accent-soft);
  color: var(--c-accent-text);
}

.header__right {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-left: auto;
}

.header__flag {
  padding: 1px var(--sp-4);
  border: 1px dashed var(--c-border-strong);
  border-radius: var(--radius-pill);
  color: var(--c-warn-text);
  font-size: var(--text-xs);
  white-space: nowrap;
}

.user {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
}

.user__name {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-3);
  font-size: var(--text-base);
}

.user__admin {
  padding: 0 var(--sp-3);
  border-radius: var(--radius-pill);
  background: var(--c-accent-soft);
  color: var(--c-accent-text);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

@media (max-width: 720px) {
  .header__inner {
    gap: var(--sp-5);
    padding: 0 var(--sp-5);
  }

  .brand__name,
  .user__name {
    display: none;
  }

  .header__flag {
    display: none;
  }
}
</style>
